import React, { useState } from "react";
import { Modal } from "../../../../shared/Modal/Modal";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import styles from "./styles.module.scss";
import { Footer } from "./shared/Footer";
import { Options } from "./shared/Options";
import { SecurityQuestion } from "./shared/SecurityQuestion";
import { Otp } from "./shared/Otp";
import { passOr } from "../../../../utilities/general";
import { useSend } from "./hooks/useSend";
import { useVerify } from "./hooks/useVerify";
import { OrPasscode } from "./shared/OrPasscode";
import { useFnCallRateLimit } from "../../../../widgets/hooks/useFnCallRateLimit";
import { useFnCallInterval } from "../../../../widgets/hooks/useFnCallInterval";
import { LOCAL_STORAGE_KEYS } from "../../../../consts/localStorage";

export function AlternativeOtp({ onClose }) {
  const { tAuth: t } = useAppTranslation.Auth({
    keyPrefix: "twoFactor.alternativeOtp",
  });

  const [answer, setAnswer] = useState("");
  const [passcode, setPasscode] = useState("");
  const [altType, setAltType] = useState("");
  const [otp, setOtp] = useState("");

  const send = useSend();
  const verify = useVerify();

  const resendRateLimit = useFnCallRateLimit({
    id: LOCAL_STORAGE_KEYS.altTwoFactorRateLimit,
    maxAttempts: 3,
    initialAttemptsAmount: 0,
    cooldownId: LOCAL_STORAGE_KEYS.altTwoFactorCooldownTimestamp,
    cooldownMs: 15 * 60 * 1000,
    message: t("info.resendLimit"),
  });

  const resendCooldown = useFnCallInterval({
    id: LOCAL_STORAGE_KEYS.altTwoFactorResendUntil,
    cdSeconds: 30,
  });

  const retry = () => {
    resendRateLimit.call(() =>
      resendCooldown.callAsync(() => send.retry({ altType, answer, passcode })),
    );
  };

  return (
    <Modal
      isOpen
      footerNoBorder
      size="small"
      onClose={onClose}
      shouldCloseOnOverlayClick={false}
      contentClassName={styles.root}
      header={<Modal.Title>{t("title")}</Modal.Title>}
      footer={
        <Footer
          onCancel={onClose}
          sendOtp={passOr(!send.isSuccess, null, () => ({
            initiate: () =>
              send.initiate({
                altType,
                answer: answer?.trim(),
                passcode: passcode?.trim(),
              }),
            isLoading: send.isLoading,
            isDisabled: (!answer?.trim() && !passcode?.trim()) || !altType,
          }))}
          verifyOtp={passOr(send.isSuccess, null, () => ({
            initiate: () => verify.initiate(otp, altType),
            isLoading: verify.isLoading,
            isDisabled: !otp?.trim(),
          }))}
        />
      }
    >
      <Options value={altType} onChange={setAltType} />
      <SecurityQuestion
        answer={answer}
        setAnswer={setAnswer}
        isDisabled={send.isSuccess}
      />
      <OrPasscode
        value={passcode}
        setValue={setPasscode}
        isDisabled={send.isSuccess}
      />
      {send.isSuccess && (
        <Otp
          value={otp}
          onChange={setOtp}
          onResend={retry}
          isResending={send.isRetrying}
          sentTo={send.sentTo}
          resendCd={resendCooldown.cd}
        />
      )}
    </Modal>
  );
}
